/**
 * The rotating waiting copy shared by the generation waits (plan generation's
 * banner, the substitution overlay).
 *
 * The stage label above these lines stays plain, because it is doing real
 * work: telling someone what is happening. This copy is not, so it can carry
 * the personality the dashboard greetings have. It also rotates, which
 * matters more than the wit does: a line that changes is the cheapest proof
 * that something is still happening.
 *
 * The first entry always shows first, so the expectation gets set before any
 * charm arrives.
 */
export const OPENING_LINE = 'This usually takes a minute or two';

/**
 * Split by household the way the dashboard greetings are.
 *
 * A joke about overruling the toddler lobby is charming to a parent and
 * faintly alienating to someone cooking for one, and the kid jokes need actual
 * kids to land. Household comes from the plan's own profile snapshot, so it is
 * the household as of that plan and needs no extra fetch.
 *
 * Broad jokes rather than plausible-sounding fake steps: "Preheating the oven"
 * would read as a real stage and quietly claim something untrue, whereas
 * nobody believes we are negotiating with their four year old.
 *
 * Snark over reassurance. Lines like "your future self says thanks" are the
 * ones that read as filler, because reassurance is exactly what someone stuck
 * waiting has already stopped believing. What works is specific and a little
 * self-deprecating: counting the pans, admitting we nearly suggested spaghetti
 * again. The joke is about the kitchen or about us, never about a promise the
 * plan then has to keep, which is why nothing here says what will or will not
 * be in it beyond the obvious.
 */
const COMMON_LINES = [
  'Better than staring into the fridge',
  'Takeout can wait a minute longer',
  'Saving you from the 5pm scramble',
  "Hope you're hungry",
  "Almost time to stop wondering what's for dinner",
  "Nobody's eating cereal for dinner this week",
  'Somewhere, a takeout menu is getting nervous',
  'That freezer burrito can stay frozen',
  'Dinner roulette is officially cancelled',
  'Side effects may include compliments',
  'This is your last dinner decision for a while',
  'The fridge light can stay off',
  'Making you look extremely organized',
  'No mystery casseroles, we promise',
  'What even is a hotdish?',
  'Your kitchen is about to get interesting',
  'Politely ignoring the back of your fridge',
  'Counting how many pans this will dirty',
  'Resisting the urge to suggest spaghetti again',
  'Doing the math on leftovers',
  "Making sure it isn't tacos every night, or...?",
  "Pretending we didn't see the expired yogurt",
  'Quietly judging your spice rack',
  'Rejecting the first three ideas on principle',
  'A few ideas have been quietly discarded',
  'Thursday is being difficult',
  'Trying to make Monday less Monday',
  'Sneaking in one suspiciously easy night',
  'Giving the chicken a night off',
  'Nobody has time to julienne on a Wednesday',
  'Nothing here is getting deconstructed',
  'No dish will be described as elevated',
  "Assuming you don't own a blowtorch",
  'Weighing dinner against the pile in the sink',
  'Fighting the urge to put eggs on everything',
  'Yes, there is a plan for the vegetables',
  'Something green will appear, eventually',
  'Cheese has been factored in',
  'Nobody is making their own stock this week',
  'Nothing that needs cheesecloth',
  'Working around the pan you never replaced',
  'Wondering whether that counts as a vegetable',
  'Do you even own a Dutch oven?',
  'Do you even own a meat thermometer?',
  'Do you even own a wok?',
  'Choosing not to ask about your knife situation',
  'Hoping the good sheet pan is clean',
  'Making peace with your smallest cutting board',
  'Fennel is on thin ice',
  'Deciding Wednesday deserves better',
  'Nothing too ambitious, define ambitious',
  "Ooh, that one's good",
  'The garlic amounts are not a typo',
  'Quick, by our standards',
  "You're going to love this, statistically",
  'No repeats, unless it was really good',
  'Nobody has to fold in the cheese',
  'Sous vide is not happening on a Tuesday',
  'Clarified butter, absolutely not',
  "Spatchcocking is somebody else's hobby",
  'No flambé, your smoke detector has been through enough',
  'No chiffonade, no brunoise, no nonsense',
  'Wondering who actually owns tweezers for food',
  'Foam is not a food group',
  'Microgreens remain entirely optional',
  'Slate plates are a crime, we agree',
  'No dish here is a culinary journey',
  'Deciding against carrots three ways',
  'Your sourdough starter can sit this one out',
  'Skipping the little bowls of pre-measured everything',
  'Nothing has to rest for exactly eleven minutes',
  'Skipping the four paragraphs about our trip to Tuscany',
  "You guys have been asking for this, allegedly",
  'Not a hack, just dinner',
  'Butter boards were a mistake',
  'Baked feta had its moment, respectfully',
  'Chili crisp is not a personality',
  'No cheese pull, no ring light, no problem',
  'Nobody is going viral tonight',
  'The recipe is right here, not in the comments',
  'Smash the potatoes, not the like button',
  'No thirty second video hiding three hours of work',
  'Nothing here needs a slow-motion pour',
  'Save this for later, or just cook it',
  "My sous chef's name is Claude",
];

const SOLO_LINES = [
  'The only vote that counts is yours',
  'Cooking for one, done properly',
  "Tomorrow's lunch is sorted too",
  'No one to please but you',
  'Nobody to judge your portion sizes',
  'No one is counting how much cheese goes on',
  'Standing at the counter still counts as dinner',
  'Yes, you will be eating this again tomorrow',
  'Nobody to notice you skipped the garnish',
  'The last portion is yours, obviously',
  'No one to pick the onions out',
  'Nobody has to know you licked the spoon',
  'Do you even own a second plate?',
  'Portion sizes are merely a suggestion',
];

const HOUSEHOLD_LINES = [
  'Nobody has to vote on dinner tonight',
  "Someone's going to ask for seconds",
  'The what-do-you-want-for-dinner talk is over',
  'Quicker than the group chat could agree',
  'Settling the great casserole debate',
  'Everyone gets fed, nobody gets consulted',
  'The dinner committee has been disbanded',
  'Vetoes are not being accepted this week',
  'Overruling at least one strong opinion',
  'Bracing for exactly one complaint',
  "Someone will still ask what's for dinner",
  "Someone will say they aren't hungry, then eat",
  'Democracy is cancelled, dinner is served',
  'Do you even own enough matching plates?',
  "Someone will call it interesting, that's fine",
];

const KIDS_LINES = [
  'The toddler lobby has been overruled',
  'Chicken nuggets did not make the cut',
  'Negotiating with your pickiest eater',
  'Fighting the beige food agenda',
  "At least one thing that isn't beige",
  'The sauce can go on the side, we know',
  'They will pick the onions out anyway',
  'One ew per week is within tolerance',
  'Nobody under four feet gets a vote',
  'Buttered noodles remain undefeated, but not tonight',
  'Yes, they can have it plain',
  'Hiding vegetables in plain sight',
  'Yes, it can go on the blue plate',
  'There are vegetables in it, do not tell them',
];

/**
 * The joke works by second-guessing itself, which only lands for someone who
 * eats meat. For anyone who does not, "loading up on meat" reads as a plan
 * that ignored their restrictions, and spending the wait wondering whether we
 * got that wrong is the opposite of funny. Pescatarian is in here for the same
 * reason: the setup is wrong for them too.
 */
const MEATLESS_DIETS = ['vegan', 'vegetarian', 'pescatarian'];

const MEAT_LINES = ['Loading up on meat... wait, were you the vegetarian?'];

const MEATLESS_LINES = ['Someone will ask where you get your protein'];

/** Household as of this plan, from its profile snapshot. */
export type PlanHousehold = {
  adults?: number;
  kids?: number;
  dietaryRestrictions?: string[];
};

const eatsMeat = (household?: PlanHousehold): boolean =>
  !(household?.dietaryRestrictions ?? []).some((restriction) =>
    MEATLESS_DIETS.includes(restriction.toLowerCase())
  );

export const linesFor = (
  household?: PlanHousehold,
  // The meal being waited on. Breakfast/lunch swaps drop the dinner jokes:
  // "Nobody's eating cereal for dinner this week" lands badly while someone
  // swaps their cereal breakfast. A literal filter rather than a second pool,
  // so new copy stays slot-safe by default.
  mealType?: 'breakfast' | 'lunch' | 'dinner'
): string[] => {
  const adults = household?.adults ?? 0;
  const kids = household?.kids ?? 0;
  const people = adults + kids;

  // Unknown household, which is the case in the moment before the snapshot is
  // written. Stick to lines that work for anyone rather than guess.
  const pool =
    people === 0
      ? COMMON_LINES
      : [
          ...COMMON_LINES,
          ...(people === 1 ? SOLO_LINES : HOUSEHOLD_LINES),
          ...(kids > 0 ? KIDS_LINES : []),
          ...(eatsMeat(household) ? MEAT_LINES : MEATLESS_LINES),
        ];

  return mealType && mealType !== 'dinner'
    ? pool.filter((line) => !line.toLowerCase().includes('dinner'))
    : pool;
};

/** Seconds each waiting line holds before the next one takes over. */
export const WAITING_LINE_SECONDS = 10;

export const hashSeed = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

/**
 * A shuffled bag rather than a random pick per slot.
 *
 * Picking independently each time would repeat lines, sometimes back to back,
 * which reads as the screen glitching rather than as variety. Working through
 * a shuffled copy means every line appears once before any repeats. Seeded off
 * the wait it belongs to, so the order is stable across re-renders but differs
 * per run.
 */
export const shuffledLines = (lines: string[], seed: number): string[] => {
  const pool = [...lines];
  let state = seed || 1;
  for (let i = pool.length - 1; i > 0; i--) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    const j = state % (i + 1);
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool;
};
